import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { LeaseFormData } from '../types/lease.types';

// Basic passthrough until dedicated sanitization is implemented
const sanitizeLeaseForPDF = (data: LeaseFormData): LeaseFormData => data;

// Enhanced PDF generation with intelligent page splitting
export const generateLeasePDFEnhanced = async (leaseData: LeaseFormData): Promise<Blob> => {
  try {
    // Get the template HTML from the public folder
    const templateURL = `${process.env.PUBLIC_URL || ''}/template.html`;
    
    const templateResponse = await fetch(templateURL);
    if (!templateResponse.ok) {
      throw new Error(`Failed to load template: ${templateResponse.status}`);
    }
    
    const templateHTML = await templateResponse.text();
    if (templateHTML.length === 0) {
      throw new Error('Template HTML is empty');
    }
    
    // Sanitize data to align with visible form fields (avoid demo-only fields)
    const sanitized = sanitizeLeaseForPDF(leaseData);
    // Fill the template with data
    const filledHTML = fillTemplate(templateHTML, sanitized);
    
    // Create a temporary container for rendering
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = filledHTML;
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = '8.5in';
    tempContainer.style.fontSize = '12pt';
    tempContainer.style.lineHeight = '1.5';
    tempContainer.style.fontFamily = 'Arial, sans-serif';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.padding = '1in';
    tempContainer.style.margin = '0';
    tempContainer.style.boxSizing = 'border-box';
    
    document.body.appendChild(tempContainer);
    
    // Add CSS for page break handling
    const style = document.createElement('style');
    style.textContent = `
      .page-break { page-break-after: always; }
      .avoid-break { page-break-inside: avoid; }
      h1, h2, h3, h4, h5, h6 { page-break-after: avoid; }
      p, li { orphans: 3; widows: 3; }
      section { page-break-inside: avoid; }
    `;
    document.head.appendChild(style);
    
    // Apply conditional display rules (hide optional rows/lines when values are empty)
    applyConditionalDisplay(tempContainer, sanitized);

    // Process content to add page break hints
    processContentForPageBreaks(tempContainer);
    
    // Wait for content to render
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Create PDF with Letter format
    const pdf = new jsPDF('p', 'mm', 'letter');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    // Calculate dimensions
    const pageWidthPx = Math.round((pdfWidth * 96) / 25.4);
    const pageHeightPx = Math.round((pdfHeight * 96) / 25.4);
    const contentHeightPx = pageHeightPx - 192; // Account for 1 inch margins top and bottom
    
    // Get top-level content blocks so we don't duplicate nested elements
    const sections = Array.from(tempContainer.children);
    if (sections.length === 0) {
      sections.push(tempContainer);
    }
    
    let currentPageElements: Element[] = [];
    let currentPageHeight = 0;
    let pageNum = 0;
    
    // Function to render a page
    const renderPage = async (elements: Element[]) => {
      if (elements.length === 0) return;
      
      // Create a container for this page's content
      const pageContainer = document.createElement('div');
      pageContainer.style.cssText = tempContainer.style.cssText;
      pageContainer.style.position = 'absolute';
      pageContainer.style.left = '-9999px';
      pageContainer.style.height = 'auto';
      
      // Clone elements for this page
      elements.forEach(el => {
        const clone = el.cloneNode(true) as HTMLElement;
        pageContainer.appendChild(clone);
      });
      
      document.body.appendChild(pageContainer);
      
      // Wait for render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Capture this page
      const canvas = await html2canvas(pageContainer, {
        scale: 1,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: pageWidthPx,
        windowWidth: pageWidthPx
      });
      
      // Add new page if needed
      if (pageNum > 0) {
        pdf.addPage();
      }
      
      // Add image to PDF with scaling to prevent clipping
      if (canvas.width > 0 && canvas.height > 0) {
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const mmWidth = (imgWidth * 25.4) / 96;
        const mmHeight = (imgHeight * 25.4) / 96;
        const ratio = Math.min(pdfWidth / mmWidth, pdfHeight / mmHeight, 1);
        const drawWidth = mmWidth * ratio;
        const drawHeight = mmHeight * ratio;
        const imgX = (pdfWidth - drawWidth) / 2;
        const imgY = 0;
        pdf.addImage(imgData, 'JPEG', imgX, imgY, drawWidth, drawHeight, undefined, 'FAST');
      }
      
      // Clean up
      document.body.removeChild(pageContainer);
      pageNum++;
    };
    
    // Helper to split a large section across multiple pages by its children
    const splitAndRenderSection = async (sectionEl: HTMLElement) => {
      // If the section fits on one page, render normally through the main loop
      if (sectionEl.offsetHeight <= contentHeightPx) {
        // Should not reach here normally when called, but guard anyway
        await renderPage([sectionEl]);
        return;
      }

    // Build child chunks that fit within a page
    const children = Array.from(sectionEl.children) as HTMLElement[];
      if (children.length === 0) {
        // No children to split by; render as-is (may overflow but avoids dropping content)
        await renderPage([sectionEl]);
        return;
      }

      // Hidden measurement container that mirrors page styling for accurate height checks
      const measureContainer = document.createElement('div');
      measureContainer.style.cssText = tempContainer.style.cssText;
      measureContainer.style.position = 'absolute';
      measureContainer.style.left = '-9999px';
      measureContainer.style.top = '0';
      measureContainer.style.height = 'auto';
      document.body.appendChild(measureContainer);

      let groupNodes: HTMLElement[] = [];

      // Helper to render the current group as a page within a wrapper that preserves section styling
      const flushGroup = async () => {
        if (groupNodes.length === 0) return;
        const groupWrapper = document.createElement('div');
        groupWrapper.className = (sectionEl.className || '').toString();
        groupNodes.forEach(node => groupWrapper.appendChild(node.cloneNode(true)));
        await renderPage([groupWrapper]);
        groupNodes = [];
        measureContainer.innerHTML = '';
      };

      // Build measurement wrapper with same class as section
      const newMeasureWrapper = () => {
        const wrapper = document.createElement('div');
        wrapper.className = (sectionEl.className || '').toString();
        return wrapper;
      };

      let measureWrapper = newMeasureWrapper();
      measureContainer.appendChild(measureWrapper);

      // Utility to split a long landlord-expandable paragraph into multiple pages by <br> breaks
      const splitExpandableParagraph = async (pEl: HTMLElement) => {
        const placeholder = pEl.querySelector('span.placeholder') as HTMLElement | null;
        const strong = pEl.querySelector('strong');
        if (!placeholder) {
          await renderPage([pEl]);
          return;
        }
        const parts = (placeholder.innerHTML || '').split(/<br\s*\/?>(?:\s*)/i);
        if (parts.length <= 1) {
          await renderPage([pEl]);
          return;
        }

        // Collect prefix nodes before the placeholder to preserve labels like "Additional rules:"
        const prefixNodes: Node[] = [];
        for (const node of Array.from(pEl.childNodes)) {
          if (node === placeholder) break;
          prefixNodes.push(node.cloneNode(true));
        }

        // Reset measurement wrapper for accurate packing
        measureContainer.innerHTML = '';
        let splitWrapper = newMeasureWrapper();
        measureContainer.appendChild(splitWrapper);
        let currentChunk: string[] = [];
        let isFirstChunk = true;

        const buildChunkPara = (chunkHtml: string) => {
          const p = document.createElement('p');
          p.className = (pEl.className || '').toString();
          // For the first chunk, recreate the prefix (label text or <strong> label)
          if (isFirstChunk) {
            if (strong) {
              const label = document.createElement('strong');
              label.innerHTML = strong.innerHTML;
              p.appendChild(label);
              p.appendChild(document.createTextNode(' '));
            } else if (prefixNodes.length > 0) {
              prefixNodes.forEach(n => p.appendChild(n.cloneNode(true)));
            }
          }
          const span = document.createElement('span');
          span.className = 'placeholder';
          span.innerHTML = chunkHtml;
          p.appendChild(span);
          return p;
        };

        const flushSplitChunk = async () => {
          if (currentChunk.length === 0) return;
          const groupWrapper = document.createElement('div');
          groupWrapper.className = (sectionEl.className || '').toString();
          const p = buildChunkPara(currentChunk.join('<br>'));
          groupWrapper.appendChild(p);
          await renderPage([groupWrapper]);
          isFirstChunk = false;
          splitWrapper = newMeasureWrapper();
          measureContainer.innerHTML = '';
          measureContainer.appendChild(splitWrapper);
          currentChunk = [];
        };

        for (const part of parts) {
          // Test with the new line added
          const testP = buildChunkPara((currentChunk.concat(part)).join('<br>'));
          splitWrapper.appendChild(testP);
          if (measureContainer.scrollHeight > pageHeightPx) {
            splitWrapper.removeChild(testP);
            await flushSplitChunk();
            currentChunk.push(part);
            const confirmP = buildChunkPara(currentChunk.join('<br>'));
            splitWrapper.appendChild(confirmP);
            if (measureContainer.scrollHeight > pageHeightPx) {
              // Even a single line overflows; render the original paragraph scaled
              await renderPage([pEl]);
              measureContainer.innerHTML = '';
              measureWrapper = newMeasureWrapper();
              measureContainer.appendChild(measureWrapper);
              currentChunk = [];
              break;
            }
          } else {
            currentChunk.push(part);
          }
        }
        if (currentChunk.length > 0) {
          await flushSplitChunk();
        }
        // Reset measurement context for the next sibling
        measureContainer.innerHTML = '';
        measureWrapper = newMeasureWrapper();
        measureContainer.appendChild(measureWrapper);
      };

      for (let idx = 0; idx < children.length; idx++) {
        const child = children[idx];

        // If this is a landlord-expandable block, isolate it and handle splitting if too tall
        if (child.classList && child.classList.contains('landlord-expandable')) {
          // Flush anything accumulated so far
          if (groupNodes.length > 0) {
            await flushGroup();
          }
          if (child instanceof HTMLElement && child.offsetHeight > contentHeightPx) {
            await splitExpandableParagraph(child);
          } else {
            await renderPage([child]);
          }
          // Reset measurement context
          measureContainer.innerHTML = '';
          measureWrapper = newMeasureWrapper();
          measureContainer.appendChild(measureWrapper);
          continue;
        }

        // Generic handling for any multiline/textarea-like paragraph with a placeholder
        if ((child as HTMLElement).tagName && (child as HTMLElement).tagName.toLowerCase() === 'p') {
          const placeholderSpan = child.querySelector('span.placeholder') as HTMLElement | null;
          if (placeholderSpan) {
            const html = placeholderSpan.innerHTML || '';
            const textLength = html.replace(/<[^>]*>/g, '').length;
            const isMultiline = /<br/i.test(html);
            const isLong = textLength > 220; // heuristic threshold for long textareas
            if (isMultiline || isLong) {
              if (groupNodes.length > 0) {
                await flushGroup();
              }
              // If too tall, split on <br> boundaries, else render as a dedicated page
              if ((child as HTMLElement).offsetHeight > contentHeightPx) {
                await splitExpandableParagraph(child as HTMLElement);
              } else {
                await renderPage([child]);
              }
              // Reset measurement context
              measureContainer.innerHTML = '';
              measureWrapper = newMeasureWrapper();
              measureContainer.appendChild(measureWrapper);
              continue;
            }
          }
        }

        const measureChild = child.cloneNode(true) as HTMLElement;
        measureWrapper.appendChild(measureChild);

        // Allow layout to settle for accurate height when needed
        // (microtask timing is typically enough for pure text content)
        // Check if adding this child overflows the page height (including 1in padding in container)
        if (measureContainer.scrollHeight > pageHeightPx) {
          // If nothing is in current group yet, force this oversized child on its own page
          if (groupNodes.length === 0) {
            // Attempt to split large paragraphs by line breaks; if not possible, render alone
            if (child.tagName.toLowerCase() === 'p') {
              const parts = (child.innerHTML || '').split(/<br\s*\/?>(?:\s*)/i);
              if (parts.length > 1) {
                // Split into multiple paragraphs and pack them across pages
                let splitWrapper = newMeasureWrapper();
                measureContainer.innerHTML = '';
                measureContainer.appendChild(splitWrapper);
                let currentChunk: string[] = [];

                const flushSplitChunk = async () => {
                  if (currentChunk.length === 0) return;
                  const p = document.createElement('p');
                  p.innerHTML = currentChunk.join('<br>');
                  const groupWrapper = document.createElement('div');
                  groupWrapper.className = (sectionEl.className || '').toString();
                  // Include the strong label only for the first chunk if present in original
                  const strong = child.querySelector('strong');
                  if (strong && splitWrapper.childElementCount === 0) {
                    const label = document.createElement('p');
                    label.innerHTML = strong.outerHTML;
                    groupWrapper.appendChild(label);
                  }
                  groupWrapper.appendChild(p);
                  await renderPage([groupWrapper]);
                  // Reset measure context
                  splitWrapper = newMeasureWrapper();
                  measureContainer.innerHTML = '';
                  measureContainer.appendChild(splitWrapper);
                  currentChunk = [];
                };

                for (const part of parts) {
                  const testP = document.createElement('p');
                  testP.innerHTML = (currentChunk.concat(part)).join('<br>');
                  splitWrapper.appendChild(testP);
                  if (measureContainer.scrollHeight > pageHeightPx) {
                    // Remove and flush previous chunk
                    splitWrapper.removeChild(testP);
                    await flushSplitChunk();
                    // Start new chunk with current part
                    currentChunk.push(part);
                    const confirmP = document.createElement('p');
                    confirmP.innerHTML = currentChunk.join('<br>');
                    splitWrapper.appendChild(confirmP);
                    if (measureContainer.scrollHeight > pageHeightPx) {
                      // Even a single line overflows due to extremely long word; render directly
                      await renderPage([child]);
                      measureContainer.innerHTML = '';
                      measureWrapper = newMeasureWrapper();
                      measureContainer.appendChild(measureWrapper);
                      currentChunk = [];
                      break;
                    }
                  } else {
                    currentChunk.push(part);
                  }
                }

                if (currentChunk.length > 0) {
                  await flushSplitChunk();
                }

                // Start fresh for subsequent siblings
                measureContainer.innerHTML = '';
                measureWrapper = newMeasureWrapper();
                measureContainer.appendChild(measureWrapper);
                continue;
              }
            }

            // Render this single child directly (will be scaled to fit)
            await renderPage([child]);
            // Reset measurement for the next group
            measureContainer.innerHTML = '';
            measureWrapper = newMeasureWrapper();
            measureContainer.appendChild(measureWrapper);
            continue;
          }

          // Otherwise flush the current group without the overflowing child
          measureWrapper.removeChild(measureChild);
          await flushGroup();

          // Start new group and re-measure with current child as first item
          measureWrapper = newMeasureWrapper();
          measureContainer.appendChild(measureWrapper);
          measureWrapper.appendChild(child.cloneNode(true));
          groupNodes.push(child);

          // If even this single child now overflows, render it directly to avoid loss
          if (measureContainer.scrollHeight > pageHeightPx) {
            await renderPage([child]);
            measureContainer.innerHTML = '';
            measureWrapper = newMeasureWrapper();
            measureContainer.appendChild(measureWrapper);
            groupNodes = [];
          }
        } else {
          // Fits; include in the current group
          groupNodes.push(child);
        }
      }

      // Flush any remaining nodes
      if (groupNodes.length > 0) {
        await flushGroup();
      }

      document.body.removeChild(measureContainer);
    };

    // Process sections and group them into pages
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i] as HTMLElement;
      const sectionHeight = section.offsetHeight;

      // Explicit page-break sentinel as a standalone element
      if (section.classList.contains('page-break')) {
        // Render the current page up to this point
        if (currentPageElements.length > 0) {
          await renderPage(currentPageElements);
          currentPageElements = [];
          currentPageHeight = 0;
        }
        // Skip adding the sentinel itself to a page
        continue;
      }

      // If section alone exceeds a page, split it by its children immediately
      if (sectionHeight > contentHeightPx) {
        // Flush current accumulated elements first
        if (currentPageElements.length > 0) {
          await renderPage(currentPageElements);
          currentPageElements = [];
          currentPageHeight = 0;
        }
        await splitAndRenderSection(section);
        continue;
      }

      // Check if this section would overflow the current page
      if (currentPageHeight + sectionHeight > contentHeightPx && currentPageElements.length > 0) {
        await renderPage(currentPageElements);
        currentPageElements = [];
        currentPageHeight = 0;
      }

      // Add section to current page
      currentPageElements.push(section);
      currentPageHeight += sectionHeight;
    }
    
    // Render any remaining content
    if (currentPageElements.length > 0) {
      await renderPage(currentPageElements);
    }
    
    // Clean up
    document.body.removeChild(tempContainer);
    document.head.removeChild(style);
    
    return pdf.output('blob');
  } catch (error) {
    console.error('Error generating enhanced PDF:', error);
    // Fallback to simple method
    return generateLeasePDF(leaseData);
  }
};

// Process content to add page break hints and avoid breaking in the middle of sections
const processContentForPageBreaks = (container: HTMLElement) => {
  // Add avoid-break class to important elements
  const importantElements = container.querySelectorAll('h1, h2, h3, h4, h5, h6, table, ul, ol');
  importantElements.forEach(el => {
    el.classList.add('avoid-break');
  });
  
  // Wrap related content in sections
  const headings = container.querySelectorAll('h2, h3');
  headings.forEach(heading => {
    const section = document.createElement('section');
    section.classList.add('avoid-break');
    
    let sibling = heading.nextElementSibling;
    heading.parentNode?.insertBefore(section, heading);
    section.appendChild(heading);
    
    // Move following content into section until next heading
    while (sibling && !sibling.matches('h1, h2, h3')) {
      const next = sibling.nextElementSibling;
      section.appendChild(sibling);
      sibling = next;
    }
  });
  
  // Add page breaks before major sections
  const majorSections = container.querySelectorAll('h1');
  majorSections.forEach((section, index) => {
    if (index > 0) {
      section.classList.add('page-break');
    }
  });
};

// Simple PDF generation fallback
export const generateLeasePDF = async (leaseData: LeaseFormData): Promise<Blob> => {
  try {
    // Get the template HTML from the public folder
    const templateURL = `${process.env.PUBLIC_URL || ''}/template.html`;
    
    const templateResponse = await fetch(templateURL);
    if (!templateResponse.ok) {
      throw new Error(`Failed to load template: ${templateResponse.status}`);
    }
    
    const templateHTML = await templateResponse.text();
    if (templateHTML.length === 0) {
      throw new Error('Template HTML is empty');
    }
    
    // Sanitize data to align with visible form fields (avoid demo-only fields)
    const sanitized = sanitizeLeaseForPDF(leaseData);
    // Fill the template with data
    const filledHTML = fillTemplate(templateHTML, sanitized);
    
    // Create a temporary container for rendering
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = filledHTML;
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = '8.5in';
    tempContainer.style.fontSize = '12pt';
    tempContainer.style.lineHeight = '1.5';
    tempContainer.style.fontFamily = 'Arial, sans-serif';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.padding = '0';
    tempContainer.style.margin = '0';
    
    document.body.appendChild(tempContainer);
    
    // Apply conditional display rules (hide optional rows/lines when values are empty)
    applyConditionalDisplay(tempContainer, sanitized);

    // Wait for any images to load
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Generate PDF using html2canvas and jsPDF
    const canvas = await html2canvas(tempContainer, {
      scale: 1,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: 816, // 8.5 inches at 96 DPI
      windowWidth: 816,
      onclone: (clonedDoc) => {
        const clonedContainer = clonedDoc.querySelector('div');
        if (clonedContainer) {
          clonedContainer.style.width = '8.5in';
          clonedContainer.style.position = 'relative';
          clonedContainer.style.left = '0';
        }
      }
    });
    
    // Clean up
    document.body.removeChild(tempContainer);
    
    // Create PDF
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('Canvas has zero dimensions - HTML content may not have rendered properly');
    }
    
    const pdf = new jsPDF('p', 'mm', 'letter');
    
    const imgData = canvas.toDataURL('image/jpeg', 0.85);
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    // Calculate dimensions to fit the content
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    
    const mmWidth = (imgWidth * 25.4) / 96;
    const mmHeight = (imgHeight * 25.4) / 96;
    
    // Scale to fit page
    const ratio = Math.min(pdfWidth / mmWidth, pdfHeight / mmHeight);
    const imgX = (pdfWidth - mmWidth * ratio) / 2;
    const imgY = 0;
    
    pdf.addImage(imgData, 'JPEG', imgX, imgY, mmWidth * ratio, mmHeight * ratio, undefined, 'FAST');
    
    return pdf.output('blob');
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF');
  }
};

// Hide or prune optional content so defaults don't render phantom data
const applyConditionalDisplay = (container: HTMLElement, leaseData: LeaseFormData) => {
  try {
    // Helper: remove summary table row by first-column label
    const removeSummaryRowByLabel = (label: string) => {
      const rows = Array.from(container.querySelectorAll('table tr')) as HTMLTableRowElement[];
      rows.forEach(row => {
        const firstCell = row.cells?.[0];
        if (firstCell && firstCell.textContent?.trim() === label) {
          row.parentElement?.removeChild(row);
        }
      });
    };

    // Pet Deposit (summary + detailed) — remove when zero/not set
    const petDepositAmount = leaseData.policies?.pets?.deposit || 0;
    if (!petDepositAmount) {
      removeSummaryRowByLabel('Pet Deposit');
      const secTitles = Array.from(container.querySelectorAll('.section-title')) as HTMLElement[];
      const sec6 = secTitles.find(t => t.textContent?.trim().startsWith('6. SECURITY DEPOSIT'))?.parentElement;
      if (sec6) {
        const p = Array.from(sec6.querySelectorAll('p')).find(el => /Additional Deposits:/i.test(el.textContent || ''));
        if (p) {
          p.innerHTML = p.innerHTML.replace(/<br>\s*Pet Deposit:[\s\S]*?(?=(<br>|<\/p>))/i, '');
        }
      }
    }

    // Other deposits — remove summary row and clear section text when none
  const normalizedOtherDeposits: Array<{ amount: number; description: string }> = [];
  if (Array.isArray(leaseData.deposits?.otherDeposits)) {
    leaseData.deposits.otherDeposits.forEach(item => {
      if (item?.amount && item?.description) {
        normalizedOtherDeposits.push({ amount: item.amount, description: item.description });
      }
    });
  }
    if (normalizedOtherDeposits.length === 0) {
      removeSummaryRowByLabel('Other Deposits');
      const secTitles = Array.from(container.querySelectorAll('.section-title')) as HTMLElement[];
      const sec6 = secTitles.find(t => t.textContent?.trim().startsWith('6. SECURITY DEPOSIT'))?.parentElement;
      if (sec6) {
        const p = Array.from(sec6.querySelectorAll('p')).find(el => /Other Deposits:/i.test(el.textContent || ''));
        if (p) {
          p.innerHTML = p.innerHTML.replace(/Other Deposits:\s*<span class="placeholder">[\s\S]*?<\/span>/i, 'Other Deposits: None collected');
        }
      }
    }

    // Prorated rent — remove summary row and section mention when not provided
    const hasProratedRent = !!(leaseData.rent?.prorated && leaseData.rent.prorated > 0);
    if (!hasProratedRent) {
      removeSummaryRowByLabel('Prorated First Month Rent');
      const secTitles = Array.from(container.querySelectorAll('.section-title')) as HTMLElement[];
      const sec3 = secTitles.find(t => t.textContent?.trim().startsWith('3. RENT'))?.parentElement;
      if (sec3) {
        const p = Array.from(sec3.querySelectorAll('p')).find(el => /Prorated Rent/i.test(el.textContent || ''));
        if (p) {
          p.parentElement?.removeChild(p);
        }
      }
    }

    // Additional Monthly Charges — remove "Pet Rent:" when zero
    const petRentAmount = (leaseData.rent?.petRent || leaseData.policies?.pets?.rent || 0);
    if (!petRentAmount) {
      const secTitles = Array.from(container.querySelectorAll('.section-title')) as HTMLElement[];
      const sec3 = secTitles.find(t => t.textContent?.trim().startsWith('3. RENT'))?.parentElement;
      if (sec3) {
        const p = Array.from(sec3.querySelectorAll('p')).find(el => /Additional Monthly Charges:/i.test(el.textContent || ''));
        if (p) {
          p.innerHTML = p.innerHTML.replace(/<br>\s*Pet Rent:[\s\S]*?(?=(<br>|<\/p>))/i, '');
        }
      }
    }

    // Summary table rows for Heating/Cooling when unspecified
    const heating = (leaseData.features?.hvac?.heating || '').trim();
    const cooling = (leaseData.features?.hvac?.cooling || '').trim();
    if (!heating) removeSummaryRowByLabel('Heating System');
    if (!cooling) removeSummaryRowByLabel('Cooling System');

    // Move-In Fee row — remove when zero/missing
    const moveInFeeAmount = leaseData.fees?.moveIn || 0;
    if (!moveInFeeAmount) removeSummaryRowByLabel('Move-In Fee');

  } catch (e) {
    // Non-fatal; if pruning fails, we still generate
    console.warn('applyConditionalDisplay failed:', e);
  }
};

const fillTemplate = (templateHTML: string, leaseData: LeaseFormData): string => {
  
  let filledHTML = templateHTML;
  
  // Escape basic HTML entities to avoid breaking template when injecting plain text
  const escapeHtml = (str: string): string => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  // Convert newlines to <br> for multi-line landlord-entered fields
  const formatMultiline = (str?: string): string => {
    if (!str) return '';
    return escapeHtml(str).replace(/\r\n|\r|\n/g, '<br>');
  };

  const formatYesNo = (value?: boolean): string => (value ? 'Yes' : 'No');

  const safeText = (str?: string, fallback = 'Not specified'): string => {
    if (!str) return fallback;
    return escapeHtml(str);
  };

  // Format currency with proper handling
  const formatCurrency = (amount: number | undefined): string => {
    const num = Number(amount);
    if (Number.isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  
  // Format date with proper handling
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const formatPropertyType = (type?: string): string => {
    if (!type) return 'Not specified';
    const labels: Record<string, string> = {
      apartment: 'Apartment',
      house: 'Single Family House',
      single_family: 'Single Family House',
      condo: 'Condominium',
      townhouse: 'Townhouse',
      duplex: 'Duplex',
      studio: 'Studio',
      other: 'Other',
    };
    return labels[type] || escapeHtml(type);
  };

  const formatLaundryType = (laundry?: string): string => {
    const map: Record<string, string> = {
      in_unit: 'In Unit',
      on_site: 'On Site (Shared)',
      none: 'None',
    };
    if (!laundry) return 'Not specified';
    return map[laundry] || escapeHtml(laundry);
  };
  
  // Generate tenant info block with all tenant details
  const generateTenantInfoBlock = (tenants: LeaseFormData['tenants']): string => {
    if (!tenants || tenants.length === 0) return 'No tenants specified';

    const formatPreviousAddress = (address?: { street?: string; city?: string; state?: string; zip?: string }): string => {
      if (!address || (!address.street && !address.city && !address.state && !address.zip)) return 'Not provided';
      const parts = ([address.street, address.city, address.state, address.zip].filter(Boolean) as string[]).map(escapeHtml);
      return parts.join(', ');
    };

    return tenants.map((tenant, index) => {
      const name = safeText(tenant.fullName, 'Not specified');
      const email = safeText(tenant.email, 'Not specified');
      const phone = safeText(tenant.phone, 'Not specified');
      const emergencyName = safeText(tenant.emergencyContact?.name, 'Not specified');
      const emergencyPhone = safeText(tenant.emergencyContact?.phone, 'Not specified');
      const emergencyRel = safeText(tenant.emergencyContact?.relationship, 'Not specified');
      const driversLicense = safeText(tenant.driversLicense, 'Not provided');
      const employer = safeText(tenant.employer, 'Not provided');
      const prevAddress = formatPreviousAddress(tenant.previousAddress);
      const reasonForMoving = safeText(tenant.reasonForMoving, 'Not specified');

      return `<strong>Tenant ${index + 1}${tenant.isPrimary ? ' (Primary)' : ''}:</strong><br>
      Name: ${name}<br>
      Email: ${email}<br>
      Phone: ${phone}<br>
      Emergency Contact: ${emergencyName} - ${emergencyPhone} (${emergencyRel})<br>
      Driver's License: ${driversLicense}<br>
      Employer: ${employer}<br>
      Previous Address: ${prevAddress}<br>
      Reason for Moving: ${reasonForMoving}`;
    }).join('<br><br>');
  };
  
  // Generate all tenant names
  const generateAllTenantNames = (tenants: LeaseFormData['tenants']): string => {
    if (!tenants || tenants.length === 0) return '';
    return tenants.map(t => t.fullName).filter(name => name).join(', ');
  };
  
  // Generate additional occupants list
  const generateAdditionalOccupants = (occupants: LeaseFormData['additionalOccupants']): string => {
    if (!occupants || occupants.length === 0) return 'None';
    return occupants.map(o => `${escapeHtml(o.fullName)} (Age: ${o.age}, ${escapeHtml(o.relationship)})`).join(', ');
  };

  const normalizeOtherDeposits = (): Array<{ description: string; amount: number }> => {
    const deposits: Array<{ description: string; amount: number }> = [];

    if (Array.isArray(leaseData.deposits?.otherDeposits)) {
      leaseData.deposits.otherDeposits.forEach(item => {
        const amount = Number(item?.amount) || 0;
        const description = (item?.description || '').trim();
        if (amount > 0 && description) {
          deposits.push({ amount, description });
        }
      });
    }

    return deposits;
  };

  const otherDeposits = normalizeOtherDeposits();
  const otherDepositsTotal = otherDeposits.reduce((sum, item) => sum + (item.amount || 0), 0);
  
  const totalDepositAmount =
    (leaseData.deposits?.security || 0) +
    (leaseData.policies?.pets?.deposit || 0) +
    otherDepositsTotal;

  const otherDepositsSummary = otherDeposits.length
    ? otherDeposits.map(d => `${escapeHtml(d.description)}: $${formatCurrency(d.amount || 0)}`).join('; ')
    : 'None';

  const otherDepositsList = otherDeposits.length
    ? otherDeposits.map(d => `${escapeHtml(d.description)} - $${formatCurrency(d.amount || 0)}`).join('<br>')
    : 'No other deposits collected';

  const generatePetTableRows = (): string => {
    const details = leaseData.policies?.pets?.details;
    const petsArray = details && details.length
      ? details
      : [
          {
            type: (leaseData.policies?.pets?.types || []).join(', ') || 'Pet',
            breed: (leaseData.policies?.pets as any)?.breeds,
            weight: leaseData.policies?.pets?.weightLimit,
            age: undefined,
            description: leaseData.policies?.pets?.restrictions,
            name: undefined,
          },
        ];

    if (!leaseData.policies?.pets?.allowed) {
      return '<tr><td colspan="5">No pets authorized.</td></tr>';
    }

    return petsArray.map((pet, idx) => {
      const weightText = pet?.weight ? `${pet.weight} lbs` : 'N/A';
      const ageText = pet?.age ? `${pet.age} yrs` : 'N/A';
      const notes = pet?.description || '';
      return `<tr>
        <td>${safeText(pet?.type || pet?.name || `Pet ${idx + 1}`, 'Pet')}</td>
        <td>${safeText(pet?.breed || pet?.description || 'N/A', 'N/A')}</td>
        <td>${escapeHtml(weightText)}</td>
        <td>${escapeHtml(ageText)}</td>
        <td>${safeText(notes || '', '')}</td>
      </tr>`;
    }).join('');
  };

  const petTableRows = generatePetTableRows();
  const petNotes = formatMultiline(leaseData.policies?.pets?.notes || '');
  const rentDueSummary = leaseData.rent?.dueDay
    ? `${leaseData.rent.dueDay}${leaseData.rent?.dueTime ? ` (by ${escapeHtml(leaseData.rent.dueTime)})` : ''}`
    : '';
  const rentDueTimeText = leaseData.rent?.dueTime ? `by ${escapeHtml(leaseData.rent.dueTime)}` : '';
  const keyTypesList = (leaseData.features?.access?.keyTypes || [])
    .map(k => `${escapeHtml(k.type || 'Key')}${k.count ? ` (${k.count})` : ''}`)
    .join(', ') || 'Not specified';
  const garageRemotesCount = (leaseData.features?.access?.garageRemotes ?? leaseData.features?.access?.remoteControls) || 0;
  const gateFobsCount = leaseData.features?.access?.gateFobs || 0;
  const otherAccessDevices = leaseData.features?.access?.otherAccessDevices || '';
  const accessCodes = leaseData.features?.access?.accessCodes || '';
  const accessNotes = formatMultiline(leaseData.features?.access?.notes || '');
  const accessDevicesTextParts = [
    garageRemotesCount ? `${garageRemotesCount} garage remote(s)` : '',
    gateFobsCount ? `${gateFobsCount} gate fob(s)` : '',
    otherAccessDevices ? escapeHtml(otherAccessDevices) : '',
  ].filter(Boolean);
  const accessDevicesText = accessDevicesTextParts.length ? accessDevicesTextParts.join(', ') : 'None provided';
  const formatFullAddress = (address?: { street?: string; unit?: string; city?: string; state?: string; zip?: string }) => {
    if (!address) return '';
    const parts = [
      address.street,
      address.unit ? `Unit ${address.unit}` : '',
      address.city,
      address.state,
      address.zip,
    ].filter(Boolean) as string[];
    return parts.map(escapeHtml).join(', ');
  };
  const tenantNoticeAddress = formatFullAddress(leaseData.tenantNoticeAddress) || formatFullAddress(leaseData.property?.address);

  const naIf = (condition: boolean, fallback = 'Not applicable'): string => (condition ? fallback : '');

  // Lead paint messaging aligned to applicability
  const builtBefore1978 = !!(leaseData.property?.yearBuilt && leaseData.property.yearBuilt < 1978);
  const leadDisclosureText = builtBefore1978
    ? 'This property was built before 1978 and may contain lead-based paint. Tenant acknowledges receipt of the federal "Protect Your Family From Lead in Your Home" pamphlet and lead disclosure form.'
    : 'This property was built after 1978. Lead-based paint disclosure does not apply.';
  const leadStatusText = builtBefore1978
    ? (leaseData.disclosures?.leadPaint?.knownLeadPaint === true
      ? 'Known lead-based paint present'
      : leaseData.disclosures?.leadPaint?.knownLeadPaint === false
        ? 'No known lead-based paint'
        : 'Not specified')
    : 'Not applicable (post-1978 property)';
  const leadReportsText = builtBefore1978
    ? formatMultiline(leaseData.disclosures?.leadPaint?.reports || 'None provided')
    : 'Not applicable';

  const nonRefundableFeeTotal = leaseData.fees?.nonRefundableTotal || 0;
  const nonRefundableDescription = leaseData.fees?.nonRefundableDescription || 'N/A';
  const lateFeeStatus = leaseData.rent?.lateFee?.amount && leaseData.rent.lateFee.amount > 0 ? 'Applied' : 'Not Applied';
  const utilityNoticeDays = leaseData.utilities?.billingNoticeDays ?? 30;
  const maintenanceChannels = leaseData.additionalTerms?.maintenanceCommunicationMethods;
  const maintenanceCommunicationDisplay = maintenanceChannels && maintenanceChannels.length > 0
    ? maintenanceChannels.join(', ')
    : (leaseData.additionalTerms?.maintenanceRequestMethod || 'Email to landlord');
  const maintenancePreferenceText = leaseData.additionalTerms?.communicationPreference
    ? `${leaseData.additionalTerms.communicationPreference.toUpperCase()} - ${leaseData.additionalTerms.maintenanceRequestMethod || 'Email to landlord'}`
    : '';
  const tpaOverallStatus = leaseData.disclosures?.tpa?.overallStatus;
  const derivedTPAStatus = tpaOverallStatus || (leaseData.disclosures?.tpa?.exempt ? 'exempt' : 'applies');
  const isTPAExempt = derivedTPAStatus === 'exempt';
  const leaseType = leaseData.terms?.type;
  const leaseTypeMonthCheck = leaseType === 'month-to-month' ? '✓' : '';
  const leaseTypeFixedCheck = leaseType === 'fixed' ? '✓' : '';
  const autoRenewYesCheck = leaseData.terms?.autoRenewal ? '✓' : '';
  const autoRenewNoCheck = leaseData.terms?.autoRenewal === false ? '✓' : '';
  const smokingPolicy = leaseData.policies?.smoking?.policy;
  const smokingChecks = {
    prohibited: smokingPolicy === 'not_permitted' ? '✓' : '',
    permitted: smokingPolicy === 'permitted' ? '✓' : '',
    outdoors: smokingPolicy === 'outdoors_only' ? '✓' : '',
  };
  const smokingAreas = smokingPolicy === 'outdoors_only'
    ? safeText((leaseData.policies?.smoking as any)?.designatedAreas, 'Outdoors only where designated')
    : 'N/A';

  const formatTPAStatus = (status?: 'applies' | 'exempt' | 'unknown'): string => {
    switch (status) {
      case 'applies':
        return 'Applies';
      case 'exempt':
        return 'Exempt';
      default:
        return 'Unknown';
    }
  };

  const tpaRentCapStatus = formatTPAStatus(leaseData.disclosures?.tpa?.rentCapStatus);
  const tpaJustCauseStatus = formatTPAStatus(leaseData.disclosures?.tpa?.justCauseStatus);
  const tpaExemptionDisclosure = isTPAExempt
    ? (leaseData.disclosures?.tpa?.exemptionReason || 'Owner qualifies for a statutory exemption.')
    : 'Property is not claiming an exemption.';

  const hoaInfo = (leaseData as any)?.property?.hoa || (leaseData.property as any)?.hoa || {};

  // Create mapping of placeholders to data with comprehensive coverage
  const placeholders: Record<string, string> = {
    // Dates
    '<<LEASE_CREATED_DATE>>': new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    '<<LEASE_START_DATE>>': formatDate(leaseData.terms?.startDate),
    '<<LEASE_END_DATE>>': leaseData.terms?.endDate ? formatDate(leaseData.terms.endDate) : 'N/A',
    '<<MOVE_IN_DATE>>': formatDate(leaseData.terms?.startDate),
    
    // Landlord Information
    '<<LANDLORD_FULL_NAME>>': leaseData.landlord?.fullName || '',
    '<<LANDLORD_EMAIL>>': leaseData.landlord?.email || '',
    '<<LANDLORD_PHONE>>': leaseData.landlord?.phone || '',
    '<<LANDLORD_ADDRESS>>': leaseData.landlord?.address?.street || '',
    '<<LANDLORD_CITY>>': leaseData.landlord?.address?.city || '',
    '<<LANDLORD_STATE>>': leaseData.landlord?.address?.state || '',
    '<<LANDLORD_ZIP>>': leaseData.landlord?.address?.zip || '',
    '<<LANDLORD_MANAGEMENT_COMPANY>>': safeText(leaseData.landlord?.managementCompany, 'N/A'),
    '<<LANDLORD_EMERGENCY_CONTACT>>': safeText(leaseData.landlord?.emergencyContact, 'Not provided'),
    
    // Property Information
    '<<PROPERTY_ADDRESS>>': leaseData.property?.address?.street || '',
    '<<UNIT_NUMBER>>': leaseData.property?.address?.unit || '',
    '<<CITY>>': leaseData.property?.address?.city || '',
    '<<STATE>>': leaseData.property?.address?.state || '',
    '<<ZIP>>': leaseData.property?.address?.zip || '',
    '<<PROPERTY_TYPE>>': formatPropertyType(leaseData.property?.type),
    '<<PROPERTY_BEDROOMS>>': leaseData.property?.bedrooms?.toString() || 'Not specified',
    '<<PROPERTY_BATHROOMS>>': leaseData.property?.bathrooms?.toString() || 'Not specified',
    '<<PROPERTY_SQFT>>': leaseData.property?.squareFootage?.toString() || 'Not specified',
    '<<PROPERTY_YEAR_BUILT>>': leaseData.property?.yearBuilt?.toString() || 'Not specified',
    '<<PROPERTY_DESCRIPTION>>': formatMultiline(leaseData.property?.description || 'N/A'),
    '<<HOA_STATUS>>': leaseData.property?.hasHOA ? 'Property is subject to an HOA' : 'Property is not governed by an HOA',
    '<<HOA_NAME>>': safeText(hoaInfo.name || (leaseData.property as any)?.hoaName, 'N/A'),
    '<<HOA_CONTACT>>': safeText(hoaInfo.contactInfo || (leaseData.property as any)?.hoaContact, 'N/A'),
    '<<HOA_FEE>>': formatCurrency(hoaInfo.monthlyFee || (leaseData.property as any)?.hoaFees || 0),
    
    // Tenant Information
    '<<TENANT_INFO_BLOCK>>': generateTenantInfoBlock(leaseData.tenants),
    '<<ALL_TENANT_NAMES>>': generateAllTenantNames(leaseData.tenants),
    '<<TENANT_1_FULL_NAME>>': leaseData.tenants?.[0]?.fullName || '',
    '<<TENANT_2_FULL_NAME>>': leaseData.tenants?.[1]?.fullName || '',
    
    // Financial Information
    '<<MONTHLY_RENT>>': formatCurrency(leaseData.rent?.monthlyAmount),
    '<<PRORATED_RENT>>': formatCurrency(leaseData.rent?.prorated || 0),
    '<<RENT_DUE_SUMMARY>>': rentDueSummary || `${leaseData.rent?.dueDay || '1'}`,
    '<<RENT_DUE_DAY>>': leaseData.rent?.dueDay?.toString() || '1',
    '<<RENT_DUE_TIME_TEXT>>': rentDueTimeText,
    '<<SECURITY_DEPOSIT>>': formatCurrency(leaseData.deposits?.security),
    '<<PET_DEPOSIT>>': formatCurrency(leaseData.policies?.pets?.deposit || 0),
    '<<LATE_FEE_FIXED>>': leaseData.rent?.lateFee?.type === 'fixed' ? formatCurrency(leaseData.rent.lateFee.amount) : '',
    '<<LATE_FEE_PERCENTAGE>>': leaseData.rent?.lateFee?.type === 'percentage' ? (leaseData.rent.lateFee.amount?.toString() || '0') : '',
    '<<GRACE_PERIOD>>': leaseData.rent?.lateFee?.gracePeriod?.toString() || '3',
    '<<NSF_FEE>>': formatCurrency(leaseData.fees?.nsfFee || 25),
    '<<TOTAL_DEPOSIT_AMOUNT>>': formatCurrency(totalDepositAmount),
    '<<OTHER_DEPOSITS_LIST>>': otherDepositsList,
    '<<OTHER_DEPOSITS_SUMMARY>>': otherDepositsSummary,
    '<<LATE_FEE_STATUS>>': lateFeeStatus,
    '<<NONREFUNDABLE_FEE_TOTAL>>': formatCurrency(nonRefundableFeeTotal),
    '<<NONREFUNDABLE_FEE_DESCRIPTION>>': nonRefundableDescription,

    // Lease Terms
    '<<NOTICE_DAYS>>': leaseData.terms?.noticeRequired?.toString() || '30',
    '<<TENANT_NOTICE_DAYS>>': leaseData.terms?.noticeRequired?.toString() || '30',
    '<<RENEWAL_TERMS>>': formatMultiline(leaseData.terms?.renewalTerms || 'As otherwise agreed in writing'),
    '<<RENT_INCREASE_NOTICE>>': leaseData.terms?.rentIncreaseNotice?.toString() || 'As required by law',
    '<<LEASE_MOVE_OUT_REQUIREMENTS>>': formatMultiline(leaseData.terms?.moveOutRequirements || 'Not specified'),
    '<<LEASE_TYPE_MONTH_CHECK>>': leaseTypeMonthCheck,
    '<<LEASE_TYPE_FIXED_CHECK>>': leaseTypeFixedCheck,
    '<<AUTO_RENEW_YES>>': autoRenewYesCheck,
    '<<AUTO_RENEW_NO>>': autoRenewNoCheck,

    // Property Features
    '<<KEY_COUNT>>': leaseData.features?.access?.keyCount?.toString() || '2',
    '<<KEY_TYPES_LIST>>': keyTypesList,
    '<<ACCESS_DEVICES>>': accessDevicesText,
    '<<GARAGE_REMOTES>>': garageRemotesCount.toString(),
    '<<GATE_FOBS>>': gateFobsCount.toString(),
    '<<OTHER_ACCESS_DEVICES>>': otherAccessDevices ? escapeHtml(otherAccessDevices) : 'None',
    '<<ACCESS_CODES>>': accessCodes ? escapeHtml(accessCodes) : 'None',
    '<<ACCESS_NOTES>>': accessNotes || 'N/A',
    '<<KEY_REPLACEMENT_FEE>>': formatCurrency(leaseData.features?.access?.keyReplacementFee ?? 25),
    '<<PARKING_TYPES>>': (leaseData.features?.parking?.types || []).map(escapeHtml).join(', ') || 'Not specified',
    '<<LAUNDRY_TYPE>>': formatLaundryType(leaseData.features?.laundry?.type),
    '<<APPLIANCES_INCLUDED>>': (leaseData.features?.appliances?.included || []).length ? (leaseData.features?.appliances?.included || []).map(escapeHtml).join(', ') : 'None',
    '<<FURNISHED_STATUS>>': leaseData.features?.furnished ? 'Furnished' : 'Not furnished',
    '<<FURNISHINGS_DETAILS>>': formatMultiline(leaseData.features?.furnishings || 'N/A'),
    
    // Utilities - Handle the checkbox format
    '<<ELECTRIC_LANDLORD>>': leaseData.utilities?.electric?.responsibility === 'landlord' ? '✓' : '',
    '<<ELECTRIC_TENANT>>': leaseData.utilities?.electric?.responsibility === 'tenant' ? '✓' : '',
    '<<ELECTRIC_NA>>': (!leaseData.utilities?.electric?.responsibility || leaseData.utilities.electric.responsibility === 'na') ? '✓' : '',
    '<<GAS_LANDLORD>>': leaseData.utilities?.gas?.responsibility === 'landlord' ? '✓' : '',
    '<<GAS_TENANT>>': leaseData.utilities?.gas?.responsibility === 'tenant' ? '✓' : '',
    '<<GAS_NA>>': (!leaseData.utilities?.gas?.responsibility || leaseData.utilities.gas.responsibility === 'na') ? '✓' : '',
    '<<WATER_LANDLORD>>': leaseData.utilities?.water?.responsibility === 'landlord' ? '✓' : '',
    '<<WATER_TENANT>>': leaseData.utilities?.water?.responsibility === 'tenant' ? '✓' : '',
    '<<WATER_NA>>': (!leaseData.utilities?.water?.responsibility || leaseData.utilities.water.responsibility === 'na') ? '✓' : '',
    '<<SEWER_LANDLORD>>': leaseData.utilities?.sewer?.responsibility === 'landlord' ? '✓' : '',
    '<<SEWER_TENANT>>': leaseData.utilities?.sewer?.responsibility === 'tenant' ? '✓' : '',
    '<<SEWER_NA>>': (!leaseData.utilities?.sewer?.responsibility || leaseData.utilities.sewer.responsibility === 'na') ? '✓' : '',
    '<<TRASH_LANDLORD>>': leaseData.utilities?.trash?.responsibility === 'landlord' ? '✓' : '',
    '<<TRASH_TENANT>>': leaseData.utilities?.trash?.responsibility === 'tenant' ? '✓' : '',
    '<<TRASH_NA>>': (!leaseData.utilities?.trash?.responsibility || leaseData.utilities.trash.responsibility === 'na') ? '✓' : '',
    '<<INTERNET_LANDLORD>>': leaseData.utilities?.internet?.responsibility === 'landlord' ? '✓' : '',
    '<<INTERNET_TENANT>>': leaseData.utilities?.internet?.responsibility === 'tenant' ? '✓' : '',
    '<<INTERNET_NA>>': (!leaseData.utilities?.internet?.responsibility || leaseData.utilities.internet.responsibility === 'na') ? '✓' : '',
    '<<CABLE_LANDLORD>>': leaseData.utilities?.cable?.responsibility === 'landlord' ? '✓' : '',
    '<<CABLE_TENANT>>': leaseData.utilities?.cable?.responsibility === 'tenant' ? '✓' : '',
    '<<CABLE_NA>>': (!leaseData.utilities?.cable?.responsibility || leaseData.utilities.cable.responsibility === 'na') ? '✓' : '',
    '<<LAWN_LANDLORD>>': leaseData.utilities?.lawnCare?.responsibility === 'landlord' ? '✓' : '',
    '<<LAWN_TENANT>>': leaseData.utilities?.lawnCare?.responsibility === 'tenant' ? '✓' : '',
    '<<LAWN_NA>>': (!leaseData.utilities?.lawnCare?.responsibility || leaseData.utilities.lawnCare.responsibility === 'na') ? '✓' : '',
    '<<SNOW_LANDLORD>>': leaseData.utilities?.snowRemoval?.responsibility === 'landlord' ? '✓' : '',
    '<<SNOW_TENANT>>': leaseData.utilities?.snowRemoval?.responsibility === 'tenant' ? '✓' : '',
    '<<SNOW_NA>>': (!leaseData.utilities?.snowRemoval?.responsibility || leaseData.utilities.snowRemoval.responsibility === 'na') ? '✓' : '',
    '<<UTILITY_BILLING_NOTICE_DAYS>>': utilityNoticeDays.toString(),

    // Payment methods
    '<<PAYMENT_METHODS>>': leaseData.rent?.paymentMethods?.join(', ') || 'Check or Money Order',
    '<<PAYMENT_ADDRESS>>': `${leaseData.landlord?.address?.street || ''}, ${leaseData.landlord?.address?.city || ''}, ${leaseData.landlord?.address?.state || ''} ${leaseData.landlord?.address?.zip || ''}`,
    
    // Additional occupants
    '<<ADDITIONAL_OCCUPANTS>>': generateAdditionalOccupants(leaseData.additionalOccupants),
    
    // Policies
    '<<QUIET_HOURS_START>>': leaseData.policies?.quietHours?.startTime || '10:00 PM',
    '<<QUIET_HOURS_END>>': leaseData.policies?.quietHours?.endTime || '8:00 AM',
    '<<QUIET_HOURS_ENFORCED>>': leaseData.policies?.quietHours?.enabled === undefined ? 'Not specified' : formatYesNo(leaseData.policies?.quietHours?.enabled),
    '<<GUEST_LIMIT_DAYS>>': leaseData.policies?.guests?.maxNights?.toString() || '14',
    '<<GUEST_PERIOD>>': '30',
    '<<GUESTS_ALLOWED>>': leaseData.policies?.guests?.allowed === undefined ? 'Not specified' : (leaseData.policies?.guests?.allowed ? 'Allowed' : 'Not allowed'),
    '<<TRASH_DAY>>': safeText(leaseData.utilities?.trash?.collectionDay, 'Not specified'),
    '<<SMOKING_PROHIBITED>>': smokingChecks.prohibited,
    '<<SMOKING_PERMITTED>>': smokingChecks.permitted,
    '<<SMOKING_OUTDOORS>>': smokingChecks.outdoors,
    '<<SMOKING_AREAS>>': smokingAreas,
    
    // Pets
    '<<PET_TYPE>>': leaseData.policies?.pets?.types?.join(', ') || 'N/A',
    '<<PET_BREED>>': leaseData.policies?.pets?.breeds || 'Any',
    '<<PET_WEIGHT>>': leaseData.policies?.pets?.weightLimit?.toString() || 'N/A',
    '<<PET_COUNT>>': leaseData.policies?.pets?.count?.toString() || 'Not specified',
    '<<PET_NAME>>': 'To be provided',
    '<<PET_RULES>>': formatMultiline(leaseData.policies?.pets?.restrictions || 'Standard pet rules apply'),
    '<<PET_TABLE_ROWS>>': petTableRows,
    '<<PET_NOTES>>': petNotes || 'N/A',
    
    // HVAC
    '<<HEATING_TYPE>>': leaseData.features?.hvac?.heating || 'Not specified',
    '<<COOLING_TYPE>>': leaseData.features?.hvac?.cooling || 'Not specified',
    
    // Parking
    '<<PARKING_DESCRIPTION>>': leaseData.features?.parking?.included ? 'Parking is included with this rental' : 'No parking included',
    '<<PARKING_SPACES>>': leaseData.features?.parking?.spaces?.toString() || 'N/A',
    '<<PARKING_RULES>>': formatMultiline(leaseData.features?.parking?.rules || 'Standard parking rules apply'),
    
    // Maintenance
    '<<TENANT_MAINTENANCE>>': formatMultiline(leaseData.additionalTerms?.maintenanceResponsibilities) || 'Keep unit clean, replace air filters monthly, maintain yard if applicable',
    
    // Insurance
    '<<INSURANCE_MINIMUM>>': '100,000',
    
    // Disclosures
    '<<LEAD_PAINT_DISCLOSURE>>': leaseData.property?.yearBuilt && leaseData.property.yearBuilt < 1978 ? 
      'This property was built before 1978 and may contain lead-based paint. Tenant acknowledges receipt of the federal "Protect Your Family From Lead in Your Home" pamphlet and lead disclosure form.' : 
      'This property was built after 1978. Lead-based paint disclosure does not apply.',
    '<<LEAD_PAINT_STATUS>>': leadStatusText,
    '<<LEAD_PAINT_REPORTS>>': leadReportsText,
    '<<ASBESTOS_DISCLOSURE>>': leaseData.disclosures?.asbestos?.status === 'yes' ? 
      'Asbestos-containing materials have been found in this property.' : 
      leaseData.disclosures?.asbestos?.status === 'no' ? 
      'No asbestos-containing materials have been found in this property.' : 
      'The presence of asbestos-containing materials in this property is unknown.',
    '<<ASBESTOS_DETAILS>>': formatMultiline(leaseData.disclosures?.asbestos?.details || naIf(leaseData.disclosures?.asbestos?.status === 'no')),
    '<<MOLD_STATUS>>': leaseData.disclosures?.mold?.status || 'No visible mold observed',
    '<<MOLD_DETAILS>>': formatMultiline(leaseData.disclosures?.mold?.details || naIf(leaseData.disclosures?.mold?.status === 'none_visible')),
    '<<BED_BUG_HISTORY>>': leaseData.disclosures?.bedBugs?.history ? 
      `Bed bugs were reported on ${leaseData.disclosures.bedBugs.lastTreatment || 'date unknown'}` : 
      'No bed bugs reported in the past year',
    '<<BED_BUG_DETAILS>>': formatMultiline(leaseData.disclosures?.bedBugs?.details || naIf(!leaseData.disclosures?.bedBugs?.history)),
    '<<METH_CONTAMINATION_STATUS>>': leaseData.disclosures?.methContamination?.status || 'No known contamination',
    '<<METH_DETAILS>>': formatMultiline(leaseData.disclosures?.methContamination?.details || naIf(leaseData.disclosures?.methContamination?.status === 'none')),
    '<<DEATH_DISCLOSURE>>': leaseData.disclosures?.deathOnProperty?.occurred ? 'A death occurred on the property' : 'No deaths on property in past 3 years',
    '<<DEATH_DETAILS>>': formatMultiline(leaseData.disclosures?.deathOnProperty?.details || naIf(!leaseData.disclosures?.deathOnProperty?.occurred)),
    '<<MILITARY_SITE_DISCLOSURE>>': leaseData.disclosures?.militarySite?.within1Mile ? 'Property is within 1 mile of a former military training site' : 'Property is not within 1 mile of any known former military training sites',
    '<<MILITARY_SITE_DETAILS>>': formatMultiline(leaseData.disclosures?.militarySite?.details || naIf(!leaseData.disclosures?.militarySite?.within1Mile)),
    '<<FLOOD_ZONE_STATUS>>': leaseData.disclosures?.floodZone?.inFloodZone ? 'Property is located in a flood hazard zone' : 'Property is not in a known flood hazard zone',
    '<<FLOOD_ZONE_DETAILS>>': formatMultiline((leaseData.disclosures as any)?.floodZone?.details || naIf(!leaseData.disclosures?.floodZone?.inFloodZone)),
    '<<EARTHQUAKE_ZONE>>': leaseData.disclosures?.earthquakeFault?.inZone ? 'Property is in an earthquake fault zone' : 'Property is not in a known earthquake fault zone',
    '<<EARTHQUAKE_DETAILS>>': formatMultiline(leaseData.disclosures?.earthquakeFault?.details || naIf(!leaseData.disclosures?.earthquakeFault?.inZone)),
    '<<NOISE_ISSUES_STATUS>>': formatYesNo(leaseData.disclosures?.noiseIssues?.hasIssues),
    '<<NOISE_ISSUES_DETAILS>>': formatMultiline(leaseData.disclosures?.noiseIssues?.details || naIf(!leaseData.disclosures?.noiseIssues?.hasIssues)),
    '<<PEST_CONTROL_STATUS>>': formatYesNo(leaseData.disclosures?.pestControl?.regularTreatment),
    '<<PEST_CONTROL_FREQUENCY>>': leaseData.disclosures?.pestControl?.frequency || naIf(!leaseData.disclosures?.pestControl?.regularTreatment, 'Not applicable'),
    '<<SHARED_UTILITIES_DISCLOSURE>>': leaseData.utilities?.sharedUtilities?.hasShared ? 
      formatMultiline(`Shared utilities: ${leaseData.utilities.sharedUtilities.description || 'Details to be provided'}`) : '',
    '<<SHARED_UTILITIES_FULL_DISCLOSURE>>': leaseData.utilities?.sharedUtilities?.hasShared ? 
      formatMultiline(`This property has shared utilities. ${leaseData.utilities.sharedUtilities.description || ''} Allocation method: ${leaseData.utilities.sharedUtilities.allocationMethod || 'To be determined'}`) : 
      'This property does not have shared utilities.',
    '<<TPA_DISCLOSURE>>': isTPAExempt ?
      `This property is exempt from the Tenant Protection Act because: ${leaseData.disclosures?.tpa?.exemptionReason || 'See exemption reason on file'}` :
      derivedTPAStatus === 'unknown'
        ? 'Tenant Protection Act applicability is under review.'
        : 'This property is subject to the Tenant Protection Act of 2019. Just-cause eviction requirements and rent increase limitations apply.',
    '<<TPA_RENT_CAP_STATUS>>': tpaRentCapStatus,
    '<<TPA_JUST_CAUSE_STATUS>>': tpaJustCauseStatus,
    '<<TPA_EXEMPTION_DISCLOSURE>>': tpaExemptionDisclosure,
    
    // Additional Terms
    '<<ADDITIONAL_RULES>>': formatMultiline(leaseData.additionalTerms?.rules || ''),
    '<<ADDITIONAL_PROPERTY_RULES>>': formatMultiline(leaseData.additionalTerms?.rules || ''),
    '<<MAINTENANCE_COMMUNICATION_METHODS>>': maintenancePreferenceText || maintenanceCommunicationDisplay,
    '<<ADDITIONAL_MOVE_OUT_REQUIREMENTS>>': formatMultiline(leaseData.additionalTerms?.moveOutRequirements || 'None specified'),
    '<<EMERGENCY_PROCEDURES>>': formatMultiline(leaseData.additionalTerms?.emergencyProcedures || 'Not provided'),
    '<<SPECIAL_PROVISIONS>>': formatMultiline(leaseData.additionalTerms?.specialProvisions || 'None provided'),
    
    // Calculated amounts
    '<<PET_RENT>>': formatCurrency(leaseData.rent?.petRent || leaseData.policies?.pets?.rent || 0),
    '<<TOTAL_MONTHLY_RENT>>': formatCurrency(
      (leaseData.rent?.monthlyAmount || 0) + 
      (leaseData.rent?.petRent || leaseData.policies?.pets?.rent || 0)
    ),
    
    // Notice addresses
    '<<LANDLORD_NOTICE_ADDRESS>>': `${leaseData.landlord?.address?.street || ''}, ${leaseData.landlord?.address?.city || ''}, ${leaseData.landlord?.address?.state || ''} ${leaseData.landlord?.address?.zip || ''}`,
    '<<TENANT_NOTICE_ADDRESS>>': tenantNoticeAddress,
  };
  
  // Replace all placeholders
  Object.entries(placeholders).forEach(([placeholder, value]) => {
    // Escape special regex characters in the placeholder
    const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedPlaceholder, 'g');
    filledHTML = filledHTML.replace(regex, value || '');
  });
  
  // Handle pets checkbox
  if (leaseData.policies?.pets?.allowed) {
    filledHTML = filledHTML.replace(
      /<span class="checkbox"><\/span> The following pets are authorized:/g,
      '<span class="checkbox">✓</span> The following pets are authorized:'
    );
  } else {
    filledHTML = filledHTML.replace(
      /<span class="checkbox"><\/span> No pets are allowed/g,
      '<span class="checkbox">✓</span> No pets are allowed'
    );
  }
  
  // Handle late fee checkbox (fixed vs percentage)
  const lateFeeType = leaseData.rent?.lateFee?.type;
  if (lateFeeType === 'fixed') {
    // Check the fixed line: "$<amount> flat fee"
    filledHTML = filledHTML.replace(
      /<span class="checkbox"><\/span>(\s*\$<span class="placeholder">[\s\S]*?<\/span>\s*flat fee)/,
      '<span class="checkbox">✓</span>$1'
    );
  } else if (lateFeeType === 'percentage') {
    // Check the percentage line: "<amount>% of the monthly rent"
    filledHTML = filledHTML.replace(
      /<span class="checkbox"><\/span>(\s*<span class="placeholder">[\s\S]*?<\/span>% of the monthly rent)/,
      '<span class="checkbox">✓</span>$1'
    );
  }

  // Handle insurance requirement checkbox
  const insuranceRequired = leaseData.policies?.insurance?.required;
  if (insuranceRequired) {
    filledHTML = filledHTML.replace(
      /<span class="checkbox"><\/span> Tenant is REQUIRED/g,
      '<span class="checkbox">✓</span> Tenant is REQUIRED'
    );
  } else {
    filledHTML = filledHTML.replace(
      /<span class="checkbox"><\/span> Tenant is strongly encouraged/g,
      '<span class="checkbox">✓</span> Tenant is strongly encouraged'
    );
  }
  
  return filledHTML;
}; 
